"use client"

import { Button } from "@heroui/react"
import { toast } from '@/src/lib/toast'
import { useState, useEffect, useRef } from "react"
import ImageComponent from "../../ImageComponent"
import OtpInput from "./OtpInput"
import { useUserResendOtpMutation, useUserVerifyOtpMutation } from "@/src/store/rtkQueries/userAuthApi"
import { getFcmTokenCookie, setAuthCookies, setGeoCookiesFromProfile } from "@/src/utils/authCookies"
import { useRouter } from "next/navigation"

const OTP_LENGTH = 6

/** `signin-email`: OTP login from email step · `signin-unverified`: password login blocked until email verified */
export type VerifyEmailOtpSource = 'signup' | 'signin-email' | 'signin-unverified'

interface VerifyEmailOtpModalProps {
    email: string
    source: VerifyEmailOtpSource
    onClose: () => void
    /** Called after successful verify, after overlay is closed (e.g. navigate to Sign in after signup). */
    onVerifiedSuccess?: () => void
}

export default function VerifyEmailOtpModal({
    email,
    source,
    onClose,
    onVerifiedSuccess,
}: VerifyEmailOtpModalProps) {
    const router = useRouter()
    const [otpValue, setOtpValue] = useState("")
    const [userVerifyOtp, { isLoading: isSubmitting }] = useUserVerifyOtpMutation()
    const [resendOtp, { isLoading: isResending }] = useUserResendOtpMutation()
    const [cooldown, setCooldown] = useState(60)
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

    useEffect(() => {
        timerRef.current = setInterval(() => {
            setCooldown((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current!)
                    return 0
                }
                return prev - 1
            })
        }, 1000)
        return () => clearInterval(timerRef.current!)
    }, [])

    const startCooldown = () => {
        setCooldown(60)
        clearInterval(timerRef.current!)
        timerRef.current = setInterval(() => {
            setCooldown((prev) => {
                if (prev <= 1) {
                    clearInterval(timerRef.current!)
                    return 0
                }
                return prev - 1
            })
        }, 1000)
    }

    const canVerify = otpValue.length === OTP_LENGTH
    const handleResendOtp = async () => {
        try {
            const response = await resendOtp({ email: email })
            if (response?.data?.http_status_code === 200 || response?.data?.http_status_code === 201) {
                toast.success('Code resent successfully')
                setOtpValue("")
                startCooldown()
            }
        } catch (error) {
            console.log('error', error);
        }
    }

    const verifyOtpType =
        source === 'signup' ? 'account' : (source === 'signin-unverified' || source === 'signin-email') ? 'login' : 'verify'
    // console.log('source', source);

    const handleVerify = async () => {
        try {
            const response = await userVerifyOtp({ email: email, code: otpValue, type: verifyOtpType, fcm_token: getFcmTokenCookie() })
            if (response?.data?.http_status_code === 200 || response?.data?.http_status_code === 201) {
                toast.success(response?.data?.message || 'OTP verified successfully')
                setAuthCookies({ token: response?.data?.data?.token, membership: response?.data?.data?.membership })
                setGeoCookiesFromProfile(response?.data?.data?.user?.ip ?? '', response?.data?.data?.user?.country ?? '', response?.data?.data?.user?.iso_code ?? response?.data?.data?.user?.country)
                router.refresh()
                onClose()
                onVerifiedSuccess?.()
            }
        } catch (error) {
            console.log('error', error);
        }
    }
    return (
        <>
            {/* Overlay backdrop */}
            <div
                className="absolute inset-0 z-10 flex items-center justify-center bg-black/50 backdrop-blur-[2px] rounded-[inherit]"
                onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
            >
                {/* Card */}
                <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-6 p-8 flex flex-col items-center">
                    {/* Close button */}
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full text-body-color/40 hover:text-body-color hover:bg-muted-color transition-colors"
                        aria-label="Close"
                    >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                            <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                    </button>

                    {/* Email icon */}
                    <div className="flex h-30 w-30 justify-center mb-4" aria-hidden>
                        <ImageComponent
                            src="/images/common/email.png"
                            alt="Email icon"
                            object_cover={false}
                        />
                    </div>

                    <h2 className="text-2xl font-semibold text-body-color text-center">
                        Verify Your <span className="font-secondary italic">Email</span>
                    </h2>
                    <p className="text-body-color/50 text-sm text-center mt-2">
                        A {OTP_LENGTH}-digit code has been sent to{" "}
                        <span className="text-body-color font-medium">{email}</span>
                    </p>

                    <div className="mt-6">
                        <OtpInput
                            value={otpValue}
                            onChange={setOtpValue}
                            length={OTP_LENGTH}
                            classNames={{ wrapper: "flex gap-4 justify-center" }}
                            ariaLabelPrefix="Digit"
                        />
                    </div>

                    <p className="text-body-color text-sm text-center mt-4">
                        Didn&apos;t get your code?{" "}
                        {cooldown > 0 ? (
                            <span className="text-body-color/40">
                                Resend in {cooldown}s
                            </span>
                        ) : (
                            <button
                                type="button"
                                className="accent_text"
                                onClick={handleResendOtp}
                                disabled={isResending}
                            >
                                {isResending ? 'Sending...' : 'Send a new code'}
                            </button>
                        )}
                    </p>

                    <Button
                        type="button"
                        isDisabled={!canVerify || isSubmitting}
                        onClick={handleVerify}
                        className="global_btn rounded_full w-full mt-6 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {isSubmitting
                            ? 'Verifying...'
                            : source === 'signup'
                                ? 'Verify & Sign In'
                                : 'Verify'}
                    </Button>
                </div>
            </div>
        </>
    )
}
